import React, { useState } from 'react';
import { HeadingComponent } from '@nebulr-group/nblocks-react/lib/components/shared/HeadingComponent';
import { TextComponent } from '@nebulr-group/nblocks-react/lib/components/shared/TextComponent';
import { SafeTenantNameComponent } from 'src/components/tenant/safe-tenant-name/safe-tenant-name.component';
import { UserListComponent } from 'src/components/tenant/user/user-list-component/user-list.component';
import { useCurrentTenant } from 'src/shared/hooks/use-current-tenant';
import { TenantBreadcrumpsComponent } from 'src/components/tenant/breadcrumps/tenant-breadcrumps-component';
import { CopyChipComponent } from 'src/components/access-role/copy.button.component';
import { useTranslation } from 'react-i18next';
import { ActiveChipComponent } from 'src/components/shared/active-chip/active-chip.component';
import { ModalComponent } from '@nebulr-group/nblocks-react/lib/components/shared/ModalComponent';
import {
  TenantModalComponent,
  SaveArgs,
} from 'src/components/tenant/tenant-list-component/tenant.modal.component';
import {
  GetAppAuthProviderStateDocument,
  UpdateTenantDocument,
} from 'src/gql/graphql';
import { useMutation, useQuery } from '@apollo/client';
import { useCurrentApp } from 'src/shared/hooks/use-current-app';
import { NblocksButton } from '@nebulr-group/nblocks-react/lib/components/shared/NblocksButton';
import { useListCurrentFederationConnections } from 'src/shared/hooks/use-list-federation-connections';
import { ChipComponent } from '@nebulr-group/nblocks-react/lib/components/shared/ChipComponent';

const AppTenantScreen = () => {
  const { t } = useTranslation();
  const { tenant } = useCurrentTenant();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const { appId } = useCurrentApp();
  const { tenantId } = useCurrentTenant();
  const [updateTenantMutation, updateTenantMutationData] =
    useMutation(UpdateTenantDocument);

  const { data: authStateData } = useQuery(GetAppAuthProviderStateDocument, {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    variables: { id: appId! },
    skip: !appId,
  });

  const { data: federationConnectionQuery } =
    useListCurrentFederationConnections();

  const didClickModalSubmit = (args: SaveArgs) => {
    updateTenantMutation({
      variables: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        appId: appId!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        tenantId: tenantId!,
        tenant: {
          name: args.name,
          mfa: args.mfa,
          federationConnection: args.federationConnection,
        },
      },
    });
    setEditModalOpen(false);
  };

  return (
    <div className="space-y-6">
      <TenantBreadcrumpsComponent />
      <div className="relative">
        <HeadingComponent type={'h1'} size={'2xl'}>
          <SafeTenantNameComponent name={tenant?.name} />
        </HeadingComponent>
        <div className="absolute top-0 right-0">
          <NblocksButton
            onClick={() => setEditModalOpen(true)}
            type="tertiary"
            size="md"
          >
            {t('Edit workspace')}
          </NblocksButton>
        </div>
      </div>
      {tenant && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <TextComponent size="sm">{t('Workspace has MFA:')}</TextComponent>
            <ActiveChipComponent active={!!tenant.mfa} />
          </div>
          {tenant.federationConnection && federationConnectionQuery && (
            <div className="flex items-center space-x-2">
              <TextComponent size="sm">Enterprise connection</TextComponent>
              <ChipComponent type={'tertiary'}>
                {
                  federationConnectionQuery.listFederationConnections.find(
                    (c) => {
                      return c.id === tenant.federationConnection;
                    },
                  )?.name
                }
              </ChipComponent>
            </div>
          )}
        </div>
      )}
      <UserListComponent />
      <ModalComponent
        isOpen={editModalOpen}
        setIsOpen={setEditModalOpen}
        heading={'Edit workspace'}
      >
        <TenantModalComponent
          didClickSubmit={(tenant) => didClickModalSubmit(tenant)}
          didClickCancel={() => setEditModalOpen(false)}
          editTenant={tenant}
          mfaEnabled={authStateData?.getApp.mfaEnabled}
          connectionQuery={federationConnectionQuery}
        />
      </ModalComponent>
    </div>
  );
};

export { AppTenantScreen };
